import clsx from "clsx"
import { Element } from "domhandler"
import DOMPurify from "dompurify"
import React, { useMemo } from "react"
import { makeStyles } from "@material-ui/core"
import parse, { HTMLReactParserOptions, domToReact } from "html-react-parser"
import { ExternalLink } from "shared/Modules/Cordova/Components/AppLinks"

const useStyles = makeStyles((_theme) => ({
    wrapper: {
        // Based on dr.dk DevTools inspection
        "& p": {
            fontSize: "1.125rem",
            lineHeight: "1.625rem",
            color: "#333333",
            marginTop: 0,
            marginBottom: "1.5rem",
        },
        "& p:last-child": {
            marginBottom: 0,
        },
        "& ul, & ol": {
            paddingLeft: "1.5rem",
            marginBottom: "1.5rem",
        },
        "& li": {
            fontSize: "1.125rem",
            lineHeight: "1.625rem",
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
            lineHeight: 1.333,
            marginTop: "1.4em",
            marginBottom: "0.5em",
            fontWeight: 600,
            color: "#000",
        },
        "& * > img": {
            width: "100%",
            height: "auto",
            marginBottom: "1em",
        },
        "& blockquote": {
            borderLeft: "3px solid #e0e0e0",
            margin: "1em 0",
            paddingLeft: "1em",
            color: "rgba(0,0,0,0.6)",
            fontStyle: "italic",
        },
        "& strong, & b": {
            fontWeight: 600,
        },
    },
}))

const parserOptions: HTMLReactParserOptions = {
    replace: (node) => {
        // We need to replace links with our custom component so that they will open correctly under Cordova
        if (node instanceof Element && node.name === "a") {
            return (
                <ExternalLink href={node.attribs["href"]} color="primary">
                    {domToReact(node.children, parserOptions)}
                </ExternalLink>
            )
        }

        return
    },
}

type DynamicHtmlProps = Readonly<{
    html: string
    className?: string
}>

export function DynamicHtml({ html, className }: DynamicHtmlProps) {
    const classes = useStyles()

    // Even though API should always sanitize HTML from user input, we perform a client-side sanitization for defense-in-depth
    // NOTE: We disallow MathML and SVG content since they should not be needed when content comes from our rich text editor
    const sanitized = useMemo(
        () => DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, ALLOW_UNKNOWN_PROTOCOLS: false }),
        [html],
    )
    const components = useMemo(() => parse(sanitized, parserOptions), [sanitized])

    return <div className={clsx(className, classes.wrapper)}>{components}</div>
}
